import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Box,
  colours,
  IconFa,
  type SpaceType,
} from "@cruk/cruk-react-components";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useActiveSectionContext } from "@fwa/src/contexts/ActiveSectionContext";
import { camelToSnakeCase } from "@fwa/src/utils/formatUtils";

import { FormActions } from "@fwa/src/components/FormActions";

import styles from "@fwa/src/components/Editable/styles.module.css";

import { type PageType } from "@fwa/src/types";
import { isBrowser } from "@fwa/src/utils/browserUtils";

/**
 * This component is for inline editing fields where you can pass in a viewNode and an editNote.
 * It turns turn the viewNote into one big edit button which opens the editNode.
 * The editNode is wrapped modal style form with submit/cancel keyboard actions
 * */
export function Editable({
  fieldName,
  viewNode,
  editNode,
  handleSubmit,
  handleCancel,
  isLoading,
  tooltip,
  editButtonColor = "inherit",
  editButtonHidden,
  editButtonBackgroundColor,
  editButtonPosition = "top-right",
  editButtonMarginRight = "none",
  fullWidth = false,
  fadeOnHover = true,
  minWidth,
}: {
  /**
   * fieldName is used mainly for populating the aria-label and the GTM update events
   * so if this is working with form with more than one field then use a sensible name for this groups of fields
   */
  fieldName?: string;
  /**
   * viewNode is the non-editable view of the field
   */
  viewNode: ReactNode;
  /**
   * editNode is the editable view of the field
   */
  editNode: ReactNode;
  /**
   * handleSubmit: on submit clicked, MUST something truthy or the editable modal will not close
   */
  handleSubmit: () => Promise<PageType | boolean | void>;
  /**
   * handleCancel: on cancel clicked, will always close modal
   */
  handleCancel?: () => void;
  /**
   * isLoading: loading state, will disable sumbit button
   */
  isLoading: boolean;
  /**
   * editButtonColor: edit icon colour
   */
  editButtonColor?: string;
  /**
   * editButtonBackgroundColor: edit icon background colour
   */
  editButtonBackgroundColor?: string;
  /**
   * editButtonBackgroundColor: edit icon background colour
   */
  editButtonHidden?: boolean;
  /**
   * editButtonPosition: edit icon position
   */
  editButtonPosition?: "top-right" | "center";
  /**
   * editButtonMarginRight: edit icon margin from right,  used for header image form
   */
  editButtonMarginRight?: SpaceType;
  /**
   * tooltip is on hover tooltip
   */
  tooltip?: string;
  /**
   * fullWidth: Used for fields like header image when the field is full width in parent container and has no padding
   */
  fullWidth?: boolean;
  /**
   * half opacity on hover
   */
  fadeOnHover?: boolean;
  /**
   * minWidth: dialog will not be narrower than this (in px), still capped to the viewport width
   */
  minWidth?: number;
}) {
  const { trackEventGtm } = useTracking();
  const [activeSectionContext, setActiveSectionContext] =
    useActiveSectionContext();
  const isActiveSection =
    !!fieldName && activeSectionContext.activeSection === fieldName;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isOpen = isEditing || isActiveSection;
  const buttonTextColour = editButtonColor
    ? colours.includes(editButtonColor as (typeof colours)[number])
      ? `var(--clr-${editButtonColor})`
      : editButtonColor
    : "inherit";
  const buttonBackgroundColour = editButtonBackgroundColor
    ? colours.includes(editButtonBackgroundColor as (typeof colours)[number])
      ? `var(--clr-${editButtonBackgroundColor})`
      : editButtonBackgroundColor
    : "inherit";

  const closeEnhancePageHighlight = () => {
    if (activeSectionContext.activeSection) {
      setActiveSectionContext({ activeSection: null });
    }
  };

  const clamp = (value: number, max: number) =>
    Math.max(0, Math.min(value, max));

  const doCancel = () => {
    if (handleCancel) handleCancel();
    setIsEditing(false);
    closeEnhancePageHighlight();
  };

  // Keep the native <dialog> element's open state in sync with isOpen,
  // position it over the element it is editing, and lock background scroll
  useEffect(() => {
    if (!isBrowser) return undefined;

    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (!isOpen) {
      if (dialog.open) dialog.close();
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;

    // overflow:hidden alone doesn't stop background touch-scroll on iOS Safari,
    // so also pin the body in place with position:fixed while the dialog is open
    const lockBodyScroll = () => {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    };

    const unlockBodyScroll = () => {
      const scrollY = -parseFloat(document.body.style.top || "0");
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };

    const positionDialog = () => {
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;

      const dialogRect = dialog.getBoundingClientRect();
      const isMobileWidth = window.innerWidth <= 576;
      // offset by the dialog's own padding so its padded content lines up with the edited element, not the dialog's edge
      const contentElement = dialog.firstElementChild
        ? getComputedStyle(dialog.firstElementChild)
        : undefined;
      const paddingTop = parseFloat(contentElement?.paddingTop || "0") || 0;
      const paddingLeft = parseFloat(contentElement?.paddingLeft || "0") || 0;
      const contentWidth =
        Math.min(wrapperRect.width, window.innerWidth) + paddingLeft * 2;

      if (isMobileWidth) {
        // full-width CSS rule takes over sizing/positioning from here on mobile
        dialog.style.width = "auto";
        dialog.style.left = "";
        // we just need to position it vertically somewhere not too low
        const maxTop = window.innerHeight - dialogRect.height;
        dialog.style.top = `${clamp(wrapperRect.top - paddingTop, maxTop)}px`;
        return;
      }

      // position the dialog horizontally and set its width for desktop view
      dialog.style.width = `${
        minWidth
          ? Math.min(Math.max(contentWidth, minWidth), window.innerWidth)
          : Math.min(contentWidth, window.innerWidth)
      }px`;

      const contentMaxWidth =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--content-max-width",
          ),
        ) || 1020;
      const maxLeft = window.innerWidth - dialog.getBoundingClientRect().width;
      dialog.style.left =
        wrapperRect.width < contentMaxWidth
          ? `${clamp(wrapperRect.left - paddingLeft, maxLeft)}px`
          : `${Math.max(0, maxLeft / 2)}px`;

      // now position the dialog vertically based on the recalculated height
      // lets recalculate the height after setting the width
      const { height: dialogHeight } = dialog.getBoundingClientRect();
      const maxTop = window.innerHeight - dialogHeight;
      dialog.style.top = `${clamp(wrapperRect.top - paddingTop, maxTop)}px`;
      // only pull the dialog left to line up with the edited element when
      // it's narrower than the page content, otherwise centre the dialog
    };

    if (!dialog.open) {
      // measure/position needs the dialog open, so open it hidden while we scroll the edited element into view
      wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      // wait for scroll to finish before opening the dialog, otherwise it will jump to the top of the page
      setTimeout(() => {
        dialog.showModal();
        positionDialog();
        // stop scroll on the body while the dialog is open, but allow scroll on the dialog itself
        lockBodyScroll();
      }, 300);
    }

    // when window changes size recalculate the dialog position
    window.addEventListener("resize", positionDialog);
    // when modal content changes size we also need to reposition the dialog
    const resizeObserver = new ResizeObserver(positionDialog);
    if (dialog.firstElementChild)
      resizeObserver.observe(dialog.firstElementChild);

    // cleanup happens on unmount or when isOpen changes which will enable scroll on the body again
    return () => {
      unlockBodyScroll();
      window.removeEventListener("resize", positionDialog);
      resizeObserver.disconnect();
    };
  }, [isOpen, minWidth]);

  // Handles native dialog close triggers, e.g. the Escape key
  const handleDialogCancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    doCancel();
  };

  const doSubmit = () => {
    handleSubmit()
      .then((resData) => {
        // We don't want to close the modal until we know we got something back from to prove a successful API call,
        // This doesn't have to be page data it could be anything.
        // This is because we catching all errors to display error messages in the form before this point
        if (!resData) return undefined;
        if (fieldName) {
          trackEventGtm({
            event: `update_${camelToSnakeCase(fieldName || "")}`,
          });
        }
        setIsEditing(false);
        closeEnhancePageHighlight();
        return undefined;
      })
      .catch(() => {
        return undefined;
      });
  };

  const handleEditClicked = () => {
    setIsEditing(true);
  };

  const handleEditClickedKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.keyCode === 13 || e.key === "Enter") {
      e.preventDefault();
      setIsEditing(true);
    }
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={styles["editableWrapper"]}
        role="button"
        title={tooltip || "edit"}
        aria-label={
          tooltip?.toLocaleLowerCase() ||
          `edit${fieldName ? ` ${fieldName}` : ""}`
        }
        onClick={handleEditClicked}
        onKeyDown={handleEditClickedKeyDown}
        tabIndex={0}
        data-edit-button-position={editButtonPosition}
        data-is-fade-on-hover={fadeOnHover}
        data-cta-type={`edit-${fieldName ? ` ${fieldName}` : ""}`}
      >
        {viewNode}
        {editButtonHidden ? null : (
          <div
            className={styles["editButton"]}
            data-edit-button-position={editButtonPosition}
            style={{
              color: buttonTextColour,
              backgroundColor: buttonBackgroundColour,
              marginRight: `var(--spacing-${editButtonMarginRight}, 1.5rem)`,
            }}
          >
            <IconFa faIcon={faPenToSquare} />
          </div>
        )}
      </div>
      <dialog
        ref={dialogRef}
        className={styles["editableDialog"]}
        data-is-full-width={fullWidth}
        aria-label={`edit ${fieldName || ""} modal`}
        onCancel={handleDialogCancel}
        onClick={(e) => {
          // dialog element itself is the backdrop, its content isn't
          if (e.target === dialogRef.current) doCancel();
        }}
      >
        <Box padding="s">
          {editNode}
          <FormActions
            isValid={true}
            isLoading={isLoading}
            onCancel={doCancel}
            onSubmit={doSubmit}
            submitButtonType="button"
          />
        </Box>
      </dialog>
    </>
  );
}

export default Editable;
